<template>
  <mf-card-container class="mt-4" title="Modelo de aplicação">
    <p class="mb-5">
      Modelo de aplicação pelo código, qualquer consumidor com o código pode utilizar o cupom e eles possuem um número de distribuição limitado.
    </p>

    <div class="d-flex align-center coupon-configs">
      <div class="w-500">
        <h2>Código do cupom de desconto</h2>
        <p class="mb-3">Permitido somente letras maiúsculas e números, nenhum ponto ou caractere especial é aceito. Mínimo de 4 caracteres e máximo de 16.</p>
        <div class="d-flex">
          <div class="w-300 flex-grow-1">
            <v-text-field
              v-model="campaign.code"
              class="custom-input"
              outlined
              maxlength="16"
              minlength="4"
              :success="computedValidCoupon"
              :success-messages="validCouponMessage"
              :error="computedValidCoupon === false || (validations.campaign.code && validations.campaign.code.$error)"
              :error-messages="invalidCouponMessage"
              :loading="loading"
              :disabled="loading || disableFields"
              @blur="validateBenefitCode"
              @keypress="blockInvalidChar"
              @input="codeInput"
            >
              <template v-if="computedValidCoupon != null || (validations.campaign.code && validations.campaign.code.$error)" #append>
                <mf-icon fill size="20" :icon="computedValidCoupon ? 'check_circle' : 'error'" :color="computedValidCoupon ? 'verde-0' : 'vermelho-0'" />
              </template>
            </v-text-field>
          </div>
          <mf-button
            :loading="loading"
            class="ml-5 mt-2"
            text-transform="none"
            outlined
            color="marca-0-l20"
            label="Gerar código aleatório"
            :disabled="disableFields"
            @click="generateBenefitCode"
          />
        </div>
      </div>

      <div class="w-500">
        <h2>Limite de cupons de desconto</h2>
        <p class="mb-3">
          Defina a quantidade limite de distribuição dos cupons de desconto. Após atingir esse limite, os cupons de desconto não serão mais aceitos.
        </p>
        <div class="w-300">
          <mf-number-input
            v-model="campaign.limitUse"
            placeholder="6000"
            outlined
            :value="campaign.limitUse"
            :min="editableLimit || 0"
            show-arrows
            :disabled="isFinished"
            :error="validations.campaign.limitUse && validations.campaign.limitUse.$error"
            :error-messages="errorLimitUse"
            auto-format
          />
        </div>
      </div>
    </div>
    <mf-info-card
      class="mt-5"
      description="Em todas campanhas de cupons de desconto, o limite de utilização de cada cupom por CPF é apenas 1."
      max-width="682px"
    />
  </mf-card-container>
</template>

<script>
import { QUERY_VALIDATE_BENEFIT_CODE, QUERY_GENERATE_BENEFIT_CODE } from '../../../graphql'
export default {
  name: 'CouponDiscountModel',
  components: {},
  props: {
    campaign: {
      type: Object,
      default: () => {},
    },
    validations: {
      type: Object,
      default: () => {},
    },
    validCoupon: {
      type: Boolean,
      default: null,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    editableLimit: null,
  }),
  computed: {
    isFinished() {
      return this.campaign.status === 'FINISHED'
    },
    disableFields() {
      return ['FINISHED', 'ACTIVE', 'SCHEDULED'].includes(this.campaign.status)
    },
    computedValidCoupon: {
      get() {
        return this.validCoupon
      },
      set(value) {
        this.$emit('update:validCoupon', value)
      },
    },
    validCouponMessage() {
      return this.computedValidCoupon ? 'Código válido.' : ''
    },
    errorLimitUse() {
      if (!this.validations.campaign.limitUse) return ''
      if (this.validations.campaign.limitUse.$error) return 'Informe o limite de cupons de desconto.'
      return ''
    },
    invalidCouponMessage() {
      if (this.computedValidCoupon === false) {
        if (this.campaign.code.length > 16 || this.campaign.code.length < 4) return 'Código inválido, número de caracteres inválido.'
        return 'Código inválido, já utilizado em outra campanha.'
      }
      if (!this.validations.campaign.code) return ''
      if (this.validations.campaign.code.$error) {
        if (!this.validations.campaign.code.required) return 'Insira um código do cupom de desconto.'
        else if (!this.validations.campaign.code.minLength || !this.validations.campaign.code.maxLength)
          return 'Código inválido, número de caracteres inválido.'
      }
      return ''
    },
  },
  mounted() {
    if (this.campaign.limitUse && this.campaign.status !== 'DRAFT') this.editableLimit = this.campaign.limitUse
    if (!['ACTIVE', 'SCHEDULED'].includes(this.campaign.status)) this.validateBenefitCode()
  },
  methods: {
    blockInvalidChar(event) {
      const allowedCharacters = /^[0-9a-zA-Z]*$/
      if (!allowedCharacters.test(event.key)) {
        event.preventDefault()
      }
    },
    codeInput() {
      this.campaign.code = this.campaign.code.toUpperCase()
      this.computedValidCoupon = null
    },
    async validateBenefitCode() {
      if (!this.campaign.code) return
      if (this.campaign.code.length < 4) {
        this.computedValidCoupon = false
        return
      }
      if (this.computedValidCoupon) return
      try {
        this.$emit('loading', true)
        const {
          data: { validateBenefitCode },
        } = await this.$apollo.mutate({
          mutation: QUERY_VALIDATE_BENEFIT_CODE,
          fetchPolicy: 'no-cache',
          variables: {
            input: {
              code: this.campaign.code,
              type: 'DISCOUNT_COUPON_ABSOLUTE',
            },
          },
          context: {
            uri: this.$microservicesUrls['benefitsManager'],
          },
        })
        this.computedValidCoupon = validateBenefitCode
      } catch (error) {
        console.log(error)
        this.computedValidCoupon = false
      } finally {
        this.$emit('loading', false)
      }
    },
    async generateBenefitCode() {
      try {
        this.computedValidCoupon = null
        this.$emit('loading', true)
        const {
          data: { generateRandomBenefitCode },
        } = await this.$apollo.mutate({
          mutation: QUERY_GENERATE_BENEFIT_CODE,
          fetchPolicy: 'no-cache',
          context: {
            uri: this.$microservicesUrls['benefitsManager'],
          },
        })
        this.campaign.code = generateRandomBenefitCode
        this.computedValidCoupon = true
      } catch (error) {
        console.log(error)
        this.$snackbar({ message: 'Erro ao gerar código do cupom', snackbarColor: 'error' })
      } finally {
        this.$emit('loading', false)
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.ticket-container {
  span {
    color: $cinza-d10;
    font-size: 18px;
  }
}

p {
  font-size: 14px;
  color: $cinza-0;
  line-height: 20px;
}

.coupon-configs {
  gap: 72px;

  h2 {
    font-weight: 600;
    font-size: 16px;
    color: $cinza-d10;
    margin-bottom: 4px;
  }
}

.w-500 {
  max-width: 500px;
}
.w-300 {
  max-width: 300px;
}

::v-deep .custom-input input {
  color: $marca-0-l10 !important;
}

::v-deep .custom-input.success--text {
  color: $verde-0 !important;
  caret-color: $verde-0 !important;
}
</style>
